import React, { useState, useRef } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import VerifyEmail from '../components/Modals/VerifyEmail';
import { sendVerificationEmail } from '../services/apiService';
import { Modal, Button, Spinner } from 'react-bootstrap';

const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '';

function SignUp() {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);

  // Validation state
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isFirstNameValid, setIsFirstNameValid] = useState(false);
  const [isLastNameValid, setIsLastNameValid] = useState(false);

  // Alert state
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Check that the email is valid and does not contain the "+" symbol
    setIsEmailValid(emailRegex.test(email) && !email.includes('+'));
  };

  // Handle email change
  const handleEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);
    validateEmail(value);
  };

  // First name validation (no numbers or spaces)
  const validateFirstName = (firstName) => {
    const nameRegex = /^[A-Za-z]+$/; // Only allows letters, no numbers or spaces
    setIsFirstNameValid(nameRegex.test(firstName));
  };

  // Handle first name change
  const handleFirstNameChange = (event) => {
    const value = event.target.value;
    setFirstName(value);
    validateFirstName(value);
  };

  // Handle last name change
  const handleLastNameChange = (event) => {
    const value = event.target.value;
    setLastName(value);
    setIsLastNameValid(value.trim() !== '');
  };

  const handleShow = async () => {
    if (HCAPTCHA_SITE_KEY && !captchaToken) {
      setAlert({ show: true, type: 'danger', message: 'Please complete the verification challenge.' });
      return;
    }
    setLoading(true);
    setAlert({ ...alert, show: false });
    try {
      await sendVerificationEmail(email, firstName, lastName, captchaToken);
      setShowModal(true);
      setCaptchaToken('');
      captchaRef.current?.resetCaptcha();
    } catch (error) {
      setAlert({
        show: true,
        type: 'danger',
        message: error?.message || 'Failed to send verification email. Please try again. If this problem continues, please contact support at 412-231-9310',
      });
      setCaptchaToken('');
      captchaRef.current?.resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  // Function to handle verification code and show success modal
  const verifyCode = async (isValid) => {
    setShowModal(false); // Close the verification modal
    setShowSuccessModal(isValid); // Show the success modal
  };

  const isFormValid = isEmailValid && isFirstNameValid && isLastNameValid && (!HCAPTCHA_SITE_KEY || !!captchaToken);

  return (
    <div id="root" className="d-flex justify-content-center align-items-center vh-100">
      <div className="bg-white rounded shadow p-4 col-lg-3 col-sm-6">
        <img src="/images/FrameworkSpark.png" alt="Framework Spark Logo" style={{ width: '95%' }} />
        
        {alert.show && (
          <div className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
            {alert.message}
            <button type="button" className="btn-close" onClick={() => setAlert({ ...alert, show: false })}></button>
          </div>
        )}

        <div className="mb-3">
          <label htmlFor="email" className="form-label">Email address</label>
          <input
            type="email"
            className={`form-control ${isEmailValid ? '' : 'is-invalid'}`}
            value={email}
            onChange={handleEmailChange}
            placeholder="Enter your email"
          />
          {!isEmailValid && email && (
            <div className="invalid-feedback">Please enter a valid email.</div>
          )}
        </div>
        <div className="mb-3">
          <label htmlFor="firstname" className="form-label">First Name</label>
          <input
            type="text"
            className={`form-control ${isFirstNameValid ? '' : 'is-invalid'}`}
            value={firstName}
            onChange={handleFirstNameChange}
            placeholder="First Name"
          />
          {!isFirstNameValid && firstName && (
            <div className="invalid-feedback">First name must contain only letters and no spaces or numbers.</div>
          )}
        </div>
        <div className="mb-5">
          <label htmlFor="lastname" className="form-label">Last Name</label>
          <input
            type="text"
            className={`form-control ${isLastNameValid ? '' : 'is-invalid'}`}
            value={lastName}
            onChange={handleLastNameChange}
            placeholder="Last Name"
          />
          {!isLastNameValid && lastName && (
            <div className="invalid-feedback">Last name is required.</div>
          )}
        </div>
        {HCAPTCHA_SITE_KEY && (
          <div className="mb-3 d-flex justify-content-center">
            <HCaptcha
              ref={captchaRef}
              sitekey={HCAPTCHA_SITE_KEY}
              onVerify={setCaptchaToken}
              onExpire={() => setCaptchaToken('')}
            />
          </div>
        )}
        <div className="d-flex justify-content-center">

        {/* Loading Screen */}
      {loading && (
        <button className="btn btn-primary rounded-0 mt-3 mb-4">
            <Spinner animation="border" variant="primary" className="text-white" />
            <span className="ms-3">Loading...</span>
        </button>
      )}
      {!loading && (
          <button
            type="submit"
            onClick={handleShow}
            className="btn btn-primary rounded-0 mt-3 mb-4"
            disabled={!isFormValid || loading} // Disable button while loading
          >
            Create Account
          </button>
      )}
        </div>
      </div>

      {/* Verify Email Modal */}
      <VerifyEmail show={showModal} handleClose={verifyCode} title="Verify Your Email" email={email}>
        This is the content inside the modal.
      </VerifyEmail>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Verification Successful</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Your email has been successfully verified! Our product team has been notified and will now review your account details. They will verify your license information and complete the account setup process. You will receive a confirmation email once your account has been fully activated. Thank you for your patience!</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" className="rounded-0" onClick={() => setShowSuccessModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default SignUp;
